import {
  type CallhomeCamera,
  type CallhomeSiteWithRelationalData,
  type DeviceEntity,
} from '@solinkcorp/api-types';
import React, { createContext, useContext } from 'react';

/** Connection state of a live player, as reported back to the widget that hosts it. */
export type SdmLivePlayerState = 'connecting' | 'playing' | 'error';

/**
 * Why a live player is in the `error` state:
 * - `networkOffline`: the browser reports no network connection.
 * - `cameraNotAccessible`: the stream request was rejected with 401 or 403.
 * - `playbackFailed`: any other stream or playback failure.
 */
export type SdmLivePlayerErrorCode = 'networkOffline' | 'cameraNotAccessible' | 'playbackFailed';

export interface SdmLivePlayerProps {
  /** The camera to stream. */
  camera: CallhomeCamera;
  /** The device the camera is attached to. */
  device: DeviceEntity;
  /** The site the camera belongs to. */
  site: CallhomeSiteWithRelationalData;
  /**
   * Reports connection progress so the widget can show its error overlay. `errorCode` is set
   * only when `state` is `error`.
   */
  onStateChange?: (state: SdmLivePlayerState, errorCode?: SdmLivePlayerErrorCode) => void;
  /** Reports the id of the stream the player selected, or null before it selects one. */
  onStreamChange?: (streamId: string | null) => void;
  /**
   * Exposes the underlying video element. The widget reads `videoWidth`/`videoHeight` from it
   * to letterbox the feed, and the letterbox backdrop will later sample frames from it.
   */
  onVideoElement?: (element: HTMLVideoElement | null) => void;
}

export type SdmLivePlayerComponent = React.ComponentType<SdmLivePlayerProps>;

const LiveVideoContext = createContext<SdmLivePlayerComponent | null>(null);

/**
 * The host-provided live player component, or `null` when the host hasn't wired one up.
 * Callers must handle `null` — live feed widgets fall back to their unavailable overlay
 * rather than throwing, so a host without video support still renders a usable layout.
 */
export function useSdmLivePlayer(): SdmLivePlayerComponent | null {
  return useContext(LiveVideoContext);
}

export function LiveVideoProvider({
  player,
  children,
}: {
  player: SdmLivePlayerComponent;
  children: React.ReactNode;
}) {
  return <LiveVideoContext.Provider value={player}>{children}</LiveVideoContext.Provider>;
}
